import { alpha, Box, Stack, Typography } from '@mui/material';
import { WatchDutySlider } from 'components/WatchDutySlider';
import { useProgressionModel } from 'features/wildfires/hooks/useProgressionModel';
import {
  PROGRESSION_MODEL_FILL_COLORS,
  PROGRESSION_MODEL_SLIDER_HEIGHT,
  PROGRESSION_MODEL_SLIDER_STEPS,
} from 'features/wildfires/wildfire.constants';
import { useQueryParams } from 'hooks/useQueryParms';
import { useEffect } from 'react';
import { formatDate } from 'shared/dates';
import { getResponsiveFontSize } from 'shared/utils';
import { makeStyles } from 'tss-react/mui';

type ProgressionModelSliderProps = {
  modelId: number;
};

type MarkLabelProps = {
  label: string;
  date: Date;
};

const createGradient = (colors: string[]): string => {
  const colorEntries = Object.entries(colors);
  const totalSteps = colorEntries.length - 1;

  return colorEntries
    .map(([step, color]): string => {
      const percentage = (parseInt(step, 10) / totalSteps) * 100;
      return `${color} ${percentage}%`;
    })
    .join(', ');
};

const useStyles = makeStyles<{ selectedIndex: number }>()((
  theme,
  { selectedIndex },
) => {
  const colors = Object.values(PROGRESSION_MODEL_FILL_COLORS).slice(
    0,
    selectedIndex + 1,
  );
  return {
    thumb: {
      border: '1px solid white',
      backgroundColor: theme.palette.accent.main,
      '&:hover, &.Mui-active, &.Mui-focusVisible': {
        boxShadow: `0 0 0 14px ${alpha(theme.palette.accent.main, 0.16)}`,
      },
    },
    track: {
      background:
        colors.length > 1
          ? `linear-gradient(to right, ${createGradient(colors)})`
          : colors[0],
      border: 'none',
    },
  };
});

const MarkLabel = (props: MarkLabelProps): JSX.Element => {
  const { label, date } = props;

  return (
    <Stack component="span" alignItems="center">
      <Typography variant="subtitle1" component="span" color="textPrimary">
        {label}
      </Typography>
      <Typography
        variant="subtitle1"
        color="textSecondary"
        sx={(theme) => ({
          fontSize: getResponsiveFontSize(theme.typography.pxToRem(10)),
        })}
        component="span"
      >
        {formatDate(date, 'h:mm a')}
      </Typography>
    </Stack>
  );
};

export const ProgressionModelSlider = (
  props: ProgressionModelSliderProps,
): JSX.Element | null => {
  const { modelId } = props;
  const { progressionModel, modelDateRange } = useProgressionModel({
    modelId,
  });
  const [{ index }, setSelectedIndex] = useQueryParams<{ index: string }>();
  const selectedIndex = parseInt(index ?? '0', 10);
  const { classes } = useStyles({ selectedIndex });

  const dateRangeLength = modelDateRange?.length;

  useEffect(() => {
    if (typeof index !== 'undefined' || !dateRangeLength) return;
    const defaultSelectedIndex = dateRangeLength - 1;
    setSelectedIndex({ index: defaultSelectedIndex.toString() });
  }, [index, dateRangeLength, setSelectedIndex]);

  if (!progressionModel || !dateRangeLength) {
    return null;
  }

  return (
    <Stack
      direction="column"
      spacing={1}
      sx={{
        height: PROGRESSION_MODEL_SLIDER_HEIGHT,
        p: 2,
        overflow: 'hidden',
        pointerEvents: 'auto',
      }}
    >
      <WatchDutySlider
        min={0}
        max={PROGRESSION_MODEL_SLIDER_STEPS - 1}
        value={selectedIndex}
        onChange={(_, value: number | number[]): void => {
          const idx = typeof value === 'number' ? value : value[0];
          if (!modelDateRange[idx]) return;
          setSelectedIndex({ index: idx.toString() });
        }}
        classes={{ thumb: classes.thumb, track: classes.track }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        {modelDateRange.map(({ date, label }, idx) =>
          idx % 2 ? null : (
            <MarkLabel key={date.toISOString()} label={label} date={date} />
          ),
        )}
      </Box>
    </Stack>
  );
};
